import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { orientationAPI } from '@/lib/orientationApi'
import type { OrientationDossier, Recommendation } from '@/lib/orientationApi'

const GRAVITE_COLORS: Record<string, string> = {
  LEGERE: 'bg-blue-100 text-blue-800',
  MODEREE: 'bg-yellow-100 text-yellow-800',
  GRAVE: 'bg-orange-100 text-orange-800',
  CRITIQUE: 'bg-red-100 text-red-800',
}

const STATUS_COLORS: Record<string, string> = {
  SCORED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-green-100 text-green-700',
}

export default function DossierPage() {
  const { ref } = useParams<{ ref: string }>()
  const [dossier, setDossier] = useState<OrientationDossier | null>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!ref) return
    setLoading(true)
    orientationAPI.getByRef(ref)
      .then((data) => {
        // Parse besoinsJson if besoins array not present
        if (!data.besoins && data.besoinsJson) {
          try { data.besoins = JSON.parse(data.besoinsJson) } catch { data.besoins = [] }
        }
        setDossier(data)
        if (data.recommendationsJson) {
          try {
            setRecommendations(JSON.parse(data.recommendationsJson))
          } catch { /* ignore parse errors */ }
        }
      })
      .catch((err) => setError(err?.message || 'Dossier introuvable'))
      .finally(() => setLoading(false))
  }, [ref])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-neutral-500">Chargement du dossier...</p>
        </div>
      </div>
    )
  }

  if (error || !dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-neutral-900 mb-1">Dossier introuvable</h2>
          <p className="text-sm text-neutral-500 mb-4">{error || `Reference "${ref}" non trouvee`}</p>
          <Link to="/orientation" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Retour a l'orientation
          </Link>
        </div>
      </div>
    )
  }

  const createdDate = dossier.createdAt
    ? new Date(dossier.createdAt).toLocaleString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-neutral-900">Dossier d'orientation</h1>
          <p className="font-mono text-sm font-bold text-primary-700 mt-1">{dossier.orientationRef}</p>
          {createdDate && <p className="text-xs text-neutral-400 mt-1">{createdDate}</p>}
        </div>

        {/* Status + Gravite */}
        <div className="flex justify-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[dossier.status] || 'bg-neutral-100 text-neutral-700'}`}>
            {dossier.status}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${GRAVITE_COLORS[dossier.gravite] || 'bg-neutral-100 text-neutral-700'}`}>
            {dossier.gravite}
          </span>
        </div>

        {/* Details card */}
        <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
          <div className="flex justify-between items-start">
            <span className="text-sm text-neutral-500">Motif</span>
            <span className="text-sm font-medium text-neutral-900 text-right max-w-[60%]">{dossier.motif}</span>
          </div>

          {dossier.besoins && dossier.besoins.length > 0 && (
            <div>
              <span className="text-sm text-neutral-500 block mb-2">Besoins specifiques</span>
              <div className="flex flex-wrap gap-1.5">
                {dossier.besoins.map((b) => (
                  <span key={b} className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          )}

          {dossier.scoringProfile && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500">Profil de scoring</span>
              <span className="text-sm text-neutral-700">{dossier.scoringProfile}</span>
            </div>
          )}

          {dossier.chosenStructureName && (
            <div className="flex justify-between items-center pt-3 border-t border-neutral-100">
              <span className="text-sm text-neutral-500">Structure choisie</span>
              <span className="text-sm font-semibold text-success-700">{dossier.chosenStructureName}</span>
            </div>
          )}

          {dossier.serviceRequestId && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500">FHIR ServiceRequest</span>
              <span className="font-mono text-xs text-neutral-500">{dossier.serviceRequestId.slice(0, 12)}...</span>
            </div>
          )}
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-sm font-semibold text-neutral-800 mb-3">Recommandations</h3>
            <div className="space-y-3">
              {recommendations.map((rec, i) => (
                <div
                  key={rec.structureId}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    dossier.chosenStructureId === rec.structureId
                      ? 'border-success-300 bg-success-50'
                      : 'border-neutral-100 bg-neutral-50'
                  }`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-neutral-300 text-white' : 'bg-orange-300 text-white'
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{rec.name}</p>
                    <p className="text-xs text-neutral-500">{rec.distanceKm?.toFixed(1)} km</p>
                  </div>
                  {dossier.chosenStructureId === rec.structureId && (
                    <span className="text-xs text-success-700 font-medium">Choisi</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-2">
          <p className="text-xs text-neutral-400">JoinXroad Sante — Dakar, Senegal</p>
          <Link to="/orientation" className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-1 inline-block">
            Nouvelle orientation
          </Link>
        </div>
      </div>
    </div>
  )
}
